import $ from 'jquery';

import clearFormInit from './init-functions/clear-form.js';
import honeypotInit from './init-functions/honeypot.js';
import replaceReturnInit from './init-functions/replace-return.js';
import scrollToAnchorInit from './init-functions/scroll-to-anchor.js';
import togglePasswordInit from './init-functions/toggle-password.js';
import toggleStockInit from './init-functions/toggle-stock.js';
import multiDropdownInit from './init-functions/multi-dropdown.js';
import headerTabsInit from './init-functions/header-tabs.js';
import notLoggedShopSelectorInit from './init-functions/not-logged-shop-selector.js';
import notLoggedAwaitSignupOverlayInit from './init-functions/not-logged-await-signup-overlay.js';
import fancyboxInit from './init-functions/fancybox-init.js';
import imageGalleryInit from './init-functions/image-gallery-init.js';
import annotateInit from './init-functions/annotate.js';
import floatingSelectPlaceholderInit from './init-functions/floating-select-placeholder.js';
import loadIframe from './init-functions/load-iframe.js';

import { isObject, isPlainObject } from '../util/types.js';
import { resizeIframeToInnerContent } from '../util/iframe.js';
import { copyTextFromElement } from '../util/clipboard.js';
import { getOptionsFromDataset } from '../util/dataset.js';
import { doFetch } from '../remote/request.js';
import { displayEnvActionAlert } from '../mpro/modules/mpro.error-handling.js';
import { handleRemoteResponse } from '../remote/remote-response.js';
import { PartialContent } from '../remote/partial-content.js';
import { cssAnimation } from '../util/animate-css.js';
import { getElementUrl, setElementUrl } from '../util/custom.js';
import { getFormDataForElement } from '../util/data.js';
// import mpro from '../mpro';


// Lazy loaded plugin list (selector => initFunction)
const pluginSelectors = {

	/**
	 * Plugin: ScrollToAnchor
	 *
	 * @type {DynamicInitFunction<HTMLAnchorElement>}
	 */
	'a[href^="#"]': scrollToAnchorInit,

	/**
	 * Plugin: Auto-Replace return param
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'a[href], form[action], [data-href], [data-url], [data-modal], [data-modal-url], [data-drawer], [data-drawer-url], [data-mp-remote-action], [data-mp-dt-remote-action]': replaceReturnInit,

	/**
	 * Plugin: Honeypot
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'div[data-mph]': honeypotInit,

	/**
	 * Plugin: NotLogged Shop Selector
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'[data-check-shop-domains]': notLoggedShopSelectorInit,

	/**
	 * Plugin: Flex Signup Overlay
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'[data-signup-overlay]': notLoggedAwaitSignupOverlayInit,

	/**
	 * Plugin: Clear form elements
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'[data-clear-form]': clearFormInit,

	/**
	 * Plugin: Show/Hide password button
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'[data-toggle-password]': togglePasswordInit,

	/**
	 * Plugin: Show/Hide infinite stock button
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'[data-toggle-stock]': toggleStockInit,

	/**
	 * Plugin: Multi dropdown menu
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'.dropdown-menu a.dropdown-toggle': multiDropdownInit,

	/**
	 * Plugin: Header tabs
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'#header-tabs': headerTabsInit,

	/**
	 * Plugin: Default fancybox handler
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'[data-fancyBox]': fancyboxInit,

	/**
	 * Plugin: Image gallery
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'[data-image-gallery]': imageGalleryInit,

	/**
	 * Plugin: rough notation (annotate)
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'[data-annotate]': annotateInit,

	/**
	 * Plugin: select floating placeholder
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'form .form-floating select:not([data-custom-select])': floatingSelectPlaceholderInit,

	/**
	 * Plugin: iframe loader
	 *
	 * @type {DynamicInitFunction<HTMLElement>}
	 */
	'[data-load-iframe]': loadIframe,

	/**
	 * Plugin: Collapse elements
	 *
	 * @param {JQuery} $collection
	 */
	'[data-toggle=collapse]': ($collection) => {

		$collection.on('click', (e) => {
			const $trigger = $(e.currentTarget);
			let $target = $($trigger.data('target'));
			if ($target.length === 0) {
				$target = $trigger.next();
			}

			if ($trigger.attr('aria-expanded') === 'true') {
				$trigger.attr('aria-expanded', 'false');
				$target.removeClass('show');
			}
			else {
				$trigger.attr('aria-expanded', 'true');
				$target.addClass('show');
			}
		});
	},

	/**
	 * Plugin: Switch
	 *
	 * @param {JQuery} $collection
	 */
	'[data-toggle=switch]': ($collection) => {

		$collection.on('click', (e) => {
			const $trigger = $(e.currentTarget);
			let $target = $($trigger.data('target'));
			if ($target.length === 0) {
				$target = $trigger.next();
			}

			if ($trigger.attr('aria-expanded') === 'true') {
				$trigger.attr('aria-expanded', 'false');
				$trigger.show();
				$target.removeClass('show');
			}
			else {
				$trigger.attr('aria-expanded', 'true');
				$trigger.hide();
				$target.addClass('show');
			}
		});
	},

	/**
	 * Plugin: Copy text from element
	 *
	 * @param {JQuery} $collection
	 */
	'[data-copy-text]': ($collection) => {

		// Add default title attribute for each element
		for (const elem of $collection) {
			const $elem = $(elem);
			if (!$elem.attr('title')) {
				$elem.attr('title', mpro.langvar('click_to_copy'));
			}
		}

		$collection.on('click', function(e) {
			e.preventDefault();
			copyTextFromElement(this);
		});
	},

	/**
	 * Plugin: Sort elements with drag/drop
	 *
	 * @link https://sortablejs.github.io/Sortable/
	 *
	 * @param {JQuery} $collection
	 */
	'[data-sortable]': async ($collection) => {

		const { createDefaultSortable } = await import('../modules/sortable.js');

		try {

			for (const elem of $collection) {

				const $elem = $(elem);
				const options = {
					handle: '.drag-handle',
					...getOptionsFromDataset(elem, 'sortable', true),
				};

				createDefaultSortable(elem, options);
			}
		}
		catch (error) {
			console.warn('Sortable error: %o', error);
		}
	},

	/**
	 * Plugin: Sort form elements with drag/drop
	 *
	 * @link https://sortablejs.github.io/Sortable/
	 *
	 * @param {JQuery} $collection
	 */
	'[data-sortable-form]': async ($collection) => {

		const { createDefaultSortable } = await import('../modules/sortable.js');

		try {

			for (const elem of $collection) {

				const $elem = $(elem);
				const options = {
					handle: '.drag-handle',
					...getOptionsFromDataset(elem, 'sortable', true),
					onSort: (e) => {
						$elem.trigger('submit');
					},
				};

				createDefaultSortable(elem.querySelector(elem.dataset.containers), options);
			}
		}
		catch (error) {
			console.warn('Sortable error: %o', error);
		}
	},

	/**
	 * Plugin: Sort elements with drag/drop
	 *
	 * @link https://sortablejs.github.io/Sortable/
	 *
	 * @param {JQuery} $collection
	 */
	'[data-sortable-inputs]': async ($collection) => {

		const { createDefaultSortable } = await import('../modules/sortable.js');

		for (const elem of $collection) {

			const $elem = $(elem);
			const options = getOptionsFromDataset(elem, 'sortable', true);

			let sortableInputName = $elem.data('sortableInput');
			const sortableInputSelector = $elem.data('sortableInputSelector');

			if (typeof (sortableInputName) !== 'undefined') {
				sortableInputName = sortableInputName.replaceAll(/([[\]])/g, String.raw`\$1`);

				options.onUpdate = (e) => {
					if ($('[name=\'' + sortableInputName + '\']').length > 0) {

						let $sortableInputs = $elem.find('input:not([type=hidden])');
						if (typeof (sortableInputSelector) !== 'undefined') {
							$sortableInputs = $elem.find(sortableInputSelector);
						}
						$('[name=\'' + sortableInputName + '\']').val($sortableInputs.map(function() {
							// @ts-ignore
							return this.name;
						}).toArray().join(','));
						$('[name=\'' + sortableInputName + '\']').trigger('change');
					}
				};
			}

			createDefaultSortable(elem, options);
		}
	},

	/**
	 * Plugin: Sort collection with drag/drop
	 *
	 * @link https://sortablejs.github.io/Sortable/
	 *
	 * @param {JQuery} $collection
	 */
	'[data-sortable-collection]': async ($collection) => {

		const { createDefaultSortable } = await import('../modules/sortable.js');

		for (const elem of $collection) {

			const $elem = $(elem);
			const options = getOptionsFromDataset(elem, 'sortable', true);

			let sortableInputName = $elem.data('sortableInput');
			const sortableIndexSelector = $elem.data('sortableIndexSelector');

			if (typeof (sortableInputName) !== 'undefined') {
				sortableInputName = sortableInputName.replaceAll(/([[\]])/g, String.raw`\$1`);

				options.onUpdate = (e) => {
					if ($('[name=\'' + sortableInputName + '\']').length > 0) {
						const $sortableRows = $elem.find(sortableIndexSelector);
						$('[name=\'' + sortableInputName + '\']').val($sortableRows.map(function() {
							// @ts-ignore
							return this.dataset.index;
						}).toArray().join(','));
						$('[name=\'' + sortableInputName + '\']').trigger('change');
					}
				};
			}

			createDefaultSortable(elem, options);
		}
	},

	/**
	 * Checker handling
	 *
	 * @param {JQuery<HTMLInputElement>} $collection
	 */
	'input[data-check-all]': ($collection) => {

		for (const elem of $collection) {

			const $elem = $(elem);
			const containerSelector = $elem.data('checkAllContainer');
			const childrenSelector = $elem.data('checkAllChildren');
			const states = Number.parseInt($elem.data('checkAllStates'), 10) || 2;

			if (typeof (containerSelector) !== 'undefined' && typeof (childrenSelector) !== 'undefined') {
				const $container = $elem.closest(containerSelector);
				if ($container.length > 0) {
					const $children = $container.find(childrenSelector).not(':disabled').not('[readonly]');
					if ($children.length > 0) {
						let $checkedChildren = $children.filter(':checked');

						if (states === 3) {
							if ($checkedChildren.length === 0) {
								$elem.prop({
									indeterminate: false,
									checked: false,
								});
							}
							else if ($checkedChildren.length === $children.length) {
								$elem.prop({
									indeterminate: false,
									checked: true,
								});
							}
							else {
								$elem.prop({
									indeterminate: true,
									checked: false,
								});
							}
						}
						else {
							if ($checkedChildren.length === 0) {
								$elem.prop({
									indeterminate: false,
									checked: false,
								});
							}
							else {
								$elem.prop({
									indeterminate: false,
									checked: true,
								});
							}
						}

						$children
							// .off('change.check-all')
							.on('change.check-all input.check-all', (e) => {
								$checkedChildren = $children.filter(':checked');

								if (states === 3) {
									if ($checkedChildren.length === 0) {
										$elem.prop({
											indeterminate: false,
											checked: false,
										}).trigger('input');
									}
									else if ($checkedChildren.length === $children.length) {
										$elem.prop({
											indeterminate: false,
											checked: true,
										}).trigger('input');
									}
									else {
										$elem.prop({
											indeterminate: true,
											checked: false,
										}).trigger('input');
									}
								}
								// 2 states
								else {
									if ($checkedChildren.length === 0) {
										$elem.prop({
											indeterminate: false,
											checked: false,
										}).trigger('input');
									}
									else {
										$elem.prop({
											indeterminate: false,
											checked: true,
										}).trigger('input');
									}
								}
							});

						$elem
							// .off('change.check-all')
							.on('change.check-all', (e) => {
								if ($elem.prop('checked') === true) {
									$children.prop('checked', true);
								}
								else {
									$children.prop('checked', false);
								}
							});
					}
				}
			}
		}
	},

	/**
	 * Plugin: Readmore.js (lazy)
	 *
	 * @link https://jedfoster.com/Readmore.js/
	 *
	 * @param {JQuery} $collection
	 */
	'[data-read-more]': async ($collection) => {

		await import('../vendor/jquery.readmore.js');

		for (const elem of $collection) {
			const $elem = $(elem);
			const options = $elem.data('readMore') || {};
			const mergedOptions = {
				speed: 150,
				collapsedHeight: 68,
				lessLink: `<a href="javascript:void(0);">${mpro.langvar('show_less')}</a>`,
				moreLink: `<a href="javascript:void(0);">${mpro.langvar('show_more')}</a>`,
				...(isObject(options) ? options : {}),
			};
			$elem.readmore(mergedOptions);
		}
	},

	/**
	 * Plugin: Apex Charts (lazy)
	 *
	 * @link https://apexcharts.com/
	 *
	 * @param {JQuery} $collection
	 */
	'[data-apex-chart]': async ($collection) => {

		// Import the Apex Charts library
		const { 'default': ApexCharts, expandOptions } = await import('../modules/apex-charts.js');

		for (const elem of $collection) {
			const $elem = $(elem);
			let config = $elem.data('apex-chart');
			config = isObject(config) ? config : {};
			const chartConfig = expandOptions(config);

			mpro.isDev && console.log('chartConfig', $.extend(true, {}, chartConfig));

			const chart = new ApexCharts(elem, chartConfig);
			cssAnimation(elem, 'fadeIn', 'fast');
			chart.render();

			// mpro.isDev && console.log('chart', chart);
		}
	},

	/**
	 * Discount form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-discount]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-toggle-date] :checkbox').on('change', (e) => {

				const $switch = $(e.currentTarget);

				if ($switch.prop('checked')) {
					$elem.find('[data-toggle-date]').addClass('d-none').find('input');
					$elem.find('[data-date]').removeClass('d-none').find('input').prop('disabled', false);
					$elem.find('[data-time]').removeClass('d-none').find('input').prop('disabled', false);
				}
				else {
					$elem.find('[data-date]').addClass('d-none').find('input').prop('disabled', true);
					$elem.find('[data-time]').addClass('d-none').find('input').prop('disabled', true);
				}
			}).triggerHandler('change');
		}
	},

	/**
	 * Flat or percent form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-flat-or-percent]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-format-dropdown] [data-value]').on('click', (e) => {
				e.preventDefault();

				const $link = $(e.currentTarget);

				$link.addClass('active').siblings().removeClass('active');
				$elem.find('[data-bs-toggle]').html($link.html());
				$elem.find('[data-format-selector]').val($link.data('value')).trigger('change');
			});

			$elem.find('[data-format-selector]').on('change', (e) => {
				$elem.find('[data-format-flat]').toggleClass('d-none').toggleClass('last-child');
				$elem.find('[data-format-percent]').toggleClass('d-none').toggleClass('last-child');
			});
		}
	},

	/**
	 * Font form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-font]': ($collection) => {

		// Rebuild table
		const previewFont = ($familySelector, $variantSelector, $previewSelector) => {
			const frameCss = [];
			const frameContent = [];
			const frameFontStyle = [];

			const $selectedOption = $familySelector.find('option:selected').first();
			const dataSel = $selectedOption.data();
			const currentFamily = $familySelector.val();
			const currentVariant = $variantSelector.val();
			const currentStyle = dataSel.style || null;
			const fontType = dataSel.type || 'system';
			const subsets = dataSel.subsets || null;

			if (fontType === 'webfont') {
				frameCss.push('<link href=\"https://fonts.googleapis.com/css?subset=latin-ext&amp;family=' + encodeURIComponent(currentFamily) + ':' + currentVariant + (
					subsets && subsets.length > 0
						? ('&amp;subset=' + encodeURIComponent(subsets.join(',')))
						: ''
				) + '\" rel=\"stylesheet\">');
			}

			frameFontStyle.push(
				`font-family: ${currentFamily};`,
				'font-size: 27px;',
				'margin: 0;',
				'outline: 0;',
				'overflow: hidden;',
				'text-overflow: ellipsis;',
				'white-space: nowrap;',
			);

			if (currentStyle) {
				for (const [key, value] of Object.entries(currentStyle)) {
					frameFontStyle.push(`${key}: ${value};`);
				}
			}

			frameCss.push('<style>body { margin: 0; } h1 { ' + frameFontStyle.join(' ') + ' }</style>');
			frameContent.push($familySelector.find('option:selected').text());

			/* Loads font contents VERY fast */
			$previewSelector.prop('srcdoc', '<html><head>' + frameCss.join(' ') + '</head><body><h1 contenteditable spellcheck=\"false\">' + frameContent.join(' ') + '</h1></body></html>');
		};

		for (const elem of $collection) {
			const $elem = $(elem);
			const $familySelector = $elem.find('[data-family-selector]');
			const $variantSelector = $elem.find('[data-variant-selector]');
			const $previewSelector = $elem.find('[data-preview-selector]');

			$familySelector.on('change', (e) => {
				previewFont($familySelector, $variantSelector, $previewSelector);
			});

			$variantSelector.on('change', (e) => {
				previewFont($familySelector, $variantSelector, $previewSelector);
			});

			previewFont($familySelector, $variantSelector, $previewSelector);
		}
	},

	/**
	 * Checkbox list form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-checkbox-list]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			const maxChoices = $elem.find('[data-widget-checkbox-list-options]').data('maxChoices');

			if (typeof maxChoices !== 'undefined' && maxChoices > 0) {
				const $checkAll = $elem.find('.form-check-title [data-check-all]');
				const $checkboxList = $elem.find('[data-widget-checkbox-list-options] input[type=checkbox]').not($elem.find('[data-is-multi-choice] input[type=checkbox]'));
				if ($checkboxList.filter(':checked').length >= maxChoices) {
					$checkboxList.not($checkboxList.filter(':checked')).not('[data-disabled]').prop('disabled', true);
					if ($checkAll.length > 0) {
						$checkAll.prop('disabled', true);
					}
				}
				$checkboxList.on('click', (e) => {
					if ($checkboxList.filter(':checked').length >= maxChoices) {
						$checkboxList.not($checkboxList.filter(':checked')).not('[data-disabled]').prop('disabled', true);
						if ($checkAll.length > 0) {
							$checkAll.prop('disabled', true);
						}
					}
					else {
						$checkboxList.not('[data-disabled]').prop('disabled', false);
						if ($checkAll.length > 0) {
							$checkAll.prop('disabled', false);
						}
					}
				});
			}
		}
	},

	/**
	 * Quote request form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-quote-request]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-current-price]').each(function() {
				const currentPrice = $(this).find('.d-none').text();
				$(this).closest('.form-group').find('[data-price]').prop('placeholder', currentPrice);
			});

			$elem.on('change input', '[data-price], [data-quantity]', (e) => {
				const $row = $(e.currentTarget).closest('[data-widget-dynamic-row]');
				const $totalContainer = $row.find('[data-total]');
				const currentPrice = Number($row.find('[data-current-price] .d-none').text());
				const $newPrice = $row.find('input[data-price]');
				const price = Number($newPrice.val());
				const quantity = Number($row.find('input[data-quantity]').val());
				const {
					language,
					currency,
				} = mpro.config.env;

				const total = ($newPrice.val() !== '' ? price : currentPrice) * quantity;
				const formattedTotal = new Intl.NumberFormat(language.canonical_locale, {
					style: 'currency',
					currency,
				}).format(total);

				$totalContainer.find('input').val('<span class="d-none">' + total + '</span>' + formattedTotal);
				$totalContainer.html(
					$totalContainer.find('input').parent().prop('outerHTML') + '<span class="d-none">' + total + '</span>' + formattedTotal,
				);
			});

			$elem.on('select2:select', '[data-product]', (e) => {
				const $row = $(e.currentTarget).closest('[data-widget-dynamic-row]');
				const $totalContainer = $row.find('[data-total]');
				const $currentPriceContainer = $row.find('[data-current-price]');
				const $newPrice = $row.find('input[data-price]');
				const data = JSON.parse($(e.currentTarget).select2('data')[0]['text']);
				const price = Number(data?.price ?? 0);
				const quantity = Number($row.find('input[data-quantity]').val());
				const {
					language,
					currency,
				} = mpro.config.env;
				const formatedPrice = new Intl.NumberFormat(language.canonical_locale, {
					style: 'currency',
					currency,
				}).format(price);

				$newPrice.prop('placeholder', price).val('');
				$currentPriceContainer.find('input').val('<span class="d-none">' + price + '</span>' + formatedPrice);
				$currentPriceContainer.html(
					$currentPriceContainer.find('input').parent().prop('outerHTML') + '<span class="d-none">' + price + '</span>' + formatedPrice,
				);

				const total = price * quantity;
				const formatedTotal = new Intl.NumberFormat(language.canonical_locale, {
					style: 'currency',
					currency,
				}).format(total);

				$totalContainer.find('input').val('<span class="d-none">' + total + '</span>' + formatedTotal);
				$totalContainer.html(
					$totalContainer.find('input').parent().prop('outerHTML') + '<span class="d-none">' + total + '</span>' + formatedTotal,
				);
			});
		}
	},

	/**
	 * Multi checkbox form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-multi-checkbox]': ($collection) => {
		// data-disabled

		for (const elem of $collection) {
			const $elem = $(elem);
			const maxChoices = $elem.data('maxChoices');

			if (typeof maxChoices !== 'undefined' && maxChoices > 0) {
				const $checkboxList = $elem.find('input[type=checkbox]');
				if ($checkboxList.filter(':checked').length >= maxChoices) {
					$checkboxList.not($checkboxList.filter(':checked')).not('[data-disabled]').prop('disabled', true);
				}
				$checkboxList.on('click', (e) => {
					if ($checkboxList.filter(':checked').length >= maxChoices) {
						$checkboxList.not($checkboxList.filter(':checked')).not('[data-disabled]').prop('disabled', true);
					}
					else {
						$checkboxList.not('[data-disabled]').prop('disabled', false);
					}
				});
			}
		}
	},

	/**
	 * Tabbed selector form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-tabbed-selector]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			$elem.find('input:checked').closest('.form-check').addClass('active');
			$elem.on('click', 'input', (e) => {
				const $this = $(e.currentTarget);
				if ($this.prop('checked')) {
					$this.closest('.form-check').addClass('active').closest('.nav-item').siblings().find('.form-check').removeClass('active');
				}
			});
		}
	},

	/**
	 * Custom radio form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-custom-radio]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			$elem.find('input:checked').closest('.alert').addClass('bg-primary text-gray-dark');
			$elem.on('click', 'input', (e) => {
				const $this = $(e.currentTarget);
				if ($this.prop('checked')) {
					$this.closest('.alert').addClass('bg-primary text-gray-dark').siblings().removeClass('bg-primary text-gray-dark');
				}
			});
		}
	},

	/**
	 * Image choice form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-image-choice]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			const toggleChoice = $elem.data('toggle-choice');
			console.log(toggleChoice);
			$elem.find('input:checked').closest('.image-choice').addClass('active');
			$elem.find('input').each(function() {
				$(this).data('previous-state', $(this).prop('checked'));
			});

			$elem.on('click', 'input', (e) => {
				const $this = $(e.currentTarget);

				if ($this.prop('checked')) {
					if ($this.is(':checkbox')) {
						$this.closest('.image-choice').addClass('active');
					}
					else {
						if ((typeof toggleChoice !== 'undefined') && $this.data('previous-state')) {
							$this.prop('checked', false);
							$this.closest('.image-choice').removeClass('active');
						}
						else {
							$this.closest('.image-choice').addClass('active').parent().siblings().find('.image-choice').removeClass('active');
						}
						$elem.find('input').each(function() {
							$(this).data('previous-state', false);
						});
					}
				}
				else {
					if ($this.is(':checkbox')) {
						$this.closest('.image-choice').removeClass('active');
					}
				}

				$this.data('previous-state', $this.prop('checked'));
			});
		}
	},

	/**
	 * Icon choice form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-icon-choice]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			$elem.on('click', 'input', (e) => {
				const $this = $(e.currentTarget);
				if ($this.prop('checked')) {
					if ($this.is(':checkbox')) {
						$this.closest('label').removeClass('btn-light').addClass('btn-secondary');
					}
					else {
						$this.closest('label').removeClass('btn-light').addClass('btn-secondary').siblings().removeClass('btn-secondary').addClass('btn-light');
					}
				}
				else {
					if ($this.is(':checkbox')) {
						$this.closest('label').addClass('btn-light').removeClass('btn-secondary');
					}
				}
			});
		}
	},

	/**
	 * Position selector form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-position-selector]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			$elem.on('click', 'input', (e) => {
				const $this = $(e.currentTarget);
				if ($this.closest('[data-extra-position]').length > 0) {
					$elem.find('[data-vertical-position] input, [data-horizontal-position] input').prop('checked', false).prop('required', false).triggerHandler('click');
					$elem.find('[data-vertical-position] .btn, [data-horizontal-position] .btn').removeClass('btn-secondary').addClass('btn-light');
				}
				else {
					$elem.find('[data-extra-position] input').prop('checked', false).triggerHandler('click');
					$elem.find('[data-extra-position] .btn').removeClass('btn-secondary').addClass('btn-light');
				}

				if ($this.closest('[data-vertical-position]').length > 0) {
					$elem.find('[data-horizontal-position] input').prop('required', true);
					$elem.find('[data-extra-position] input').prop('required', false);
				}
				if ($this.closest('[data-horizontal-position]').length > 0) {
					$elem.find('[data-vertical-position] input').prop('required', true);
					$elem.find('[data-extra-position] input').prop('required', false);
				}
			});
		}
	},

	/**
	 * Include / exclude categories form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-include-exclude-categories]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			const $type = $elem.find('select[name*="action"],input[name*="action"]');
			$type.on('change', (e) => {
				const actions = $type.serializeArray().filter(v => v.value).reduce((all, v) => {
					all.push(v.value); return all;
				}, []);

				$elem.find('[data-include-selector]')[(actions.includes('include') ? 'removeClass' : 'addClass')]('d-none');
				$elem.find('[data-include-selector] input, [data-include-selector] select').prop('disabled', (actions.includes('include') ? false : true));
				$elem.find('[data-exclude-selector]')[(actions.includes('exclude') ? 'removeClass' : 'addClass')]('d-none');
				$elem.find('[data-exclude-selector] input, [data-exclude-selector] select').prop('disabled', (actions.includes('exclude') ? false : true));
				$elem.find('[data-dynamic-selector]')[(actions.includes('dynamic') ? 'removeClass' : 'addClass')]('d-none');
				$elem.find('[data-dynamic-selector] input, [data-dynamic-selector] select').prop('disabled', (actions.includes('dynamic') ? false : true));

				if (actions.length === 0 || actions.includes('any')) {
					$elem.find('[data-format-selector]').addClass('d-none');
					$elem.find('[data-format-selector] input, [data-format-selector] select').prop('disabled', true);
				}
				else {
					$elem.find('[data-format-selector]').removeClass('d-none');
					$elem.find('[data-format-selector] input, [data-format-selector] select').prop('disabled', false);
				}
			}).triggerHandler('change');
		}
	},

	/**
	 * Marketplace files uploader form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-marketplace-files-uploader]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			const $type = $elem.find('select[name*="action"],input[name*="action"]');
			$type.on('change', (e) => {
				const actions = $type.serializeArray().filter(v => v.value).reduce((all, v) => {
					all.push(v.value); return all;
				}, []);

				$elem.find('[data-picker-selector]')[(actions.includes('picker') ? 'removeClass' : 'addClass')]('d-none');
				$elem.find('[data-picker-selector] input, [data-picker-selector] select').prop('disabled', (actions.includes('picker') ? false : true));
				$elem.find('[data-upload-selector]')[(actions.includes('upload') ? 'removeClass' : 'addClass')]('d-none');
				// $elem.find('[data-upload-selector] [data-ajax-uploader]')[(actions.includes('upload') ? 'removeClass' : 'addClass')]('disabled');
				// console.log('asd');
				// $elem.find('[data-upload-selector] input, [data-upload-selector] select').prop('disabled', (actions.includes('upload') ? false : true));

			}).triggerHandler('change');
		}
	},

	/**
	 * Text multi lang form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-text-multi-lang]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			const $langItem = $elem.find('[data-lang-item]');

			$langItem.on('click', (e) => {
				const $target = $(e.currentTarget);
				const lang = $target.data('lang-item');
				$elem.find('[data-lang-selected]').html($target.find('img').prop('outerHTML'));
				
				const $fromWidget = $elem.find('[data-lang-input="' + lang + '"]');
				const $toWidget = $elem.find('[data-lang-input]:visible');

				const $fromClone = $fromWidget.clone(true);
				const $toClone = $toWidget.clone(true);

				$fromWidget.replaceWith($toClone);
				$toWidget.replaceWith($fromClone);
			}).triggerHandler('change');
		}
	},

	/**
	 * Slug form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-slug]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[preview_from_input]').each(function() {
				const $input = $(this);
				const targetInputName = $input.attr('preview_from_input').replaceAll(/([[\]])/g, '\\$1');
				/** @type {JQuery<HTMLTextAreaElement|HTMLInputElement>} */ // @ts-ignore
				const $targetInput = mpro.get$B().find(`input[name="${targetInputName}"],textarea[name="${targetInputName}"]`);

				if ($targetInput.length > 0) {
					$targetInput.on('keyup focus', () => {
						// @ts-ignore
						buildSlug.call(this, $input, $targetInput);
					});
				}
			})
			.on('keyup', function(e){
				const $input = $(e.currentTarget);
				const targetInputName = $input.attr('preview_from_input').replaceAll(/([[\]])/g, '\\$1');
				/** @type {JQuery<HTMLTextAreaElement|HTMLInputElement>} */ // @ts-ignore
				const $targetInput = mpro.get$B().find(`input[name="${targetInputName}"],textarea[name="${targetInputName}"]`);

				$input.data('prevent-preview', $input.val() !== '');

				if ($targetInput.length > 0) {
					// @ts-ignore
					buildSlug.call(this, $input, $targetInput);
				}
			})
			.on('blur focus', function(e){
				const $input = $(e.currentTarget);
				const targetInputName = $input.attr('preview_from_input').replaceAll(/([[\]])/g, '\\$1');
				/** @type {JQuery<HTMLTextAreaElement|HTMLInputElement>} */ // @ts-ignore
				const $targetInput = mpro.get$B().find(`input[name="${targetInputName}"],textarea[name="${targetInputName}"]`);

				if ($targetInput.length > 0) {
					// @ts-ignore
					buildSlug.call(this, $input, $targetInput);
				}
			});

			$elem.find('input').on('keydown', function(e){

				// Allow control keys like backspace, delete, tab, and arrow keys
				const controlKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
				if (controlKeys.includes(e.key)) {
					return; // Allow control keys
				}

				 // Only allow lowercase letters (a-z)
				 const isLowercaseLetter = /^[a-z0-9\-]$/.test(e.key);
				 if (!isLowercaseLetter) {
					 e.preventDefault(); // Block invalid key press
				 }
			});
		}

		function buildSlug($input, $targetInput) {
			if (!$input.data('prevent-preview')) {
				$input.val(mpro.convertToSlug($targetInput.val()));
			}
		}
	},

	/**
	 * Price adjustments form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-price-adjustments]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			const $type = $elem.find('select[name*="action"],input[name*="action"]');
			$type.on('change', (e) => {
				const actions = $type.serializeArray().filter(v => v.value).reduce((all, v) => {
					all.push(v.value); return all;
				}, []);

				$elem.find('[data-percent-adjustment-selector]')[(actions.includes('percent_adjustment') ? 'removeClass' : 'addClass')]('d-none');
				$elem.find('[data-percent-adjustment-selector] input, [data-percent-adjustment-selector] select').prop('disabled', (actions.includes('percent_adjustment') ? false : true));

				$elem.find('[data-rule-adjustment-selector]')[(actions.includes('rule_adjustment') ? 'removeClass' : 'addClass')]('d-none');
				$elem.find('[data-rule-adjustment-selector] input, [data-rule-adjustment-selector] select').prop('disabled', (actions.includes('rule_adjustment') ? false : true));

			}).triggerHandler('change');
		}
	},

	/**
	 * Include / exclude select2 form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-include-exclude-select2]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			const $type = $elem.find('select[name*="action"],input[name*="action"]');

			const toggleElements = function() {
				const actions = $type.serializeArray().filter(v => v.value).reduce((all, v) => {
					all.push(v.value); return all;
				}, []);

				$elem.find('[data-include-selector]')[(actions.includes('include') ? 'removeClass' : 'addClass')]('d-none');
				$elem.find('[data-include-selector] input, [data-include-selector] select').prop('disabled', (actions.includes('include') ? false : true));
				$elem.find('[data-exclude-selector]')[(actions.includes('exclude') ? 'removeClass' : 'addClass')]('d-none');
				$elem.find('[data-exclude-selector] input, [data-exclude-selector] select').prop('disabled', (actions.includes('exclude') ? false : true));
			};

			$type.on('change', (e) => {
				const $target = $(e.currentTarget);
				if ($target.is(':checkbox')) {
					if ($target.val() === 'exclude_all' && $target.prop('checked')) {
						$type.not($target).prop('checked', false);
					}
					else if ($target.val() !== 'exclude_all' && $target.prop('checked')) {
						$type.filter('[value=\"exclude_all\"]').prop('checked', false);
					}
				}

				toggleElements();
			});

			toggleElements();
		}
	},

	/**
	 * Collection bundled products form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-bundled-products]': async ($collection) => {

		const { enhanceSelects } = await import('../mpro/modules/mpro.select2.js');

		// Link primary with secondary select for a certain row
		const handleRow = function(row, $elem) {
			const $row = $(row);
			if ($row.length > 0) {
				const $customSelects = $row.find('select[data-custom-select]');

				// @ts-ignore
				enhanceSelects($customSelects);
			}
		};

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-widget-dynamic-row]').each(function() {
				handleRow(this, $elem);
			});

			$elem.on('dynamic-row-added', (e, row) => {
				handleRow($(row).find('[data-widget-dynamic-row]')[0], $elem);
			});
		}
	},

	/**
	 * Collection export fields form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-export-fields]': async ($collection) => {

		const { enhanceSelects } = await import('../mpro/modules/mpro.select2.js');

		// Link primary with secondary select for a certain row
		const handleRow = function(row, $elem) {
			const $row = $(row);
			if ($row.length > 0) {
				const $customSelects = $row.find('select[data-custom-select]');

				// @ts-ignore
				enhanceSelects($customSelects);
			}
		};

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-widget-dynamic-row]').each(function() {
				handleRow(this, $elem);
			});

			$elem.on('dynamic-row-added', (e, row) => {
				handleRow($(row).find('[data-widget-dynamic-row]')[0], $elem);
			});
		}
	},

	/**
	 * Collection select2 form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-dual-select2]': async ($collection) => {

		let colors = [];
		const { enhanceSelects } = await import('../mpro/modules/mpro.select2.js');

		// Link primary with secondary select for a certain row
		const handleRow = function(row, $elem) {
			const $row = $(row);

			if ($row.length > 0) {
				const $customSelects = $row.find('select[data-custom-select]');
				const $primary = $row.find('[data-primary]');
				$elem.data('primary-inputs', $elem.data('primary-inputs').add($primary));
				const $secondary = $row.find('[data-secondary]');
				// const index = $elem.find('[data-widget-dynamic-row]').index($row);
				const index = $row.prop('id').split('_').at(-1);

				// Append selection color
				if (colors.length > 0) {
					const secondaryOptions = getOptionsFromDataset($secondary[0], 'custom-select', false);
					secondaryOptions.customConfig.selectionColor = colors[index % colors.length];
					$secondary.data('customSelect', secondaryOptions);
				}

				// @ts-ignore
				enhanceSelects($customSelects);

				// $secondary.on('change', (e) => {e.stopPropagation();});

				$primary.off('change.dual-select2').on('change.dual-select2', (e, first_trigger) => {
					// Clear old values
					if (typeof first_trigger === 'undefined') {
						$secondary.val('').trigger('change');
						e.stopPropagation();
					}

					// Reinit other primary inputs with exclude ids params
					$elem.data('primary-inputs').each(function() {
						// @ts-ignore
						const $this = $(this);
						// Reinit select with new ajax data
						const select2Options = getOptionsFromDataset($this[0], 'custom-select', false);
						select2Options.ajax.data = function(params) {
							params['exclude_ids'] = $.unique($elem.data('primary-inputs').not($this).serializeArray().map(v => v.value).filter(Boolean));
							return $.extend({}, params, {
								q: params.term,
							});
						};
						$this.data('customSelect', select2Options);
						enhanceSelects($this);
					});

					// A value is selected on primary select
					// @ts-ignore
					if ($primary.val() > 0 && $primary.prop('disabled') === false) {
						$secondary.prop('disabled', false);

						// Reinit secondary select with new ajax data
						const select2Options = getOptionsFromDataset($secondary[0], 'custom-select', false);
						const link_name = $secondary.data('link');

						// Append selection color
						if (colors.length > 0) {
							select2Options.customConfig.selectionColor = colors[index % colors.length];
						}

						select2Options.ajax.data = function(params) {
							params[link_name] = $primary.val();
							return $.extend({}, params, {
								q: params.term,
							});
						};
						// $secondary.select2('destroy');
						$secondary.data('customSelect', select2Options);
						// @ts-ignore
						enhanceSelects($secondary);
					}
					else {
						$secondary.prop('disabled', true);
					}
				})
					// Use triggerHandler to avoid anti-pattern programming
					// The 'change' event should be triggered and bubbled ONLY if it makes sense
					.triggerHandler('change.dual-select2', [true]);
			}
		};

		for (const elem of $collection) {
			const $elem = $(elem);
			$elem.data('primary-inputs', $());

			$elem.find('[data-widget-dynamic-row]').each(function() {
				colors = (typeof $elem.data('colors') !== 'undefined' ? $elem.data('colors') : []);

				handleRow(this, $elem);
			});

			$elem.on('dynamic-row-added', (e, row) => {
				handleRow($(row).find('[data-widget-dynamic-row]')[0], $elem);
			});

			$elem.on('dynamic-row-before-remove', (e, row) => {
				$elem.data('primary-inputs', $elem.data('primary-inputs').not($(row).find('[data-widget-dynamic-row] [data-primary]')));
			});
		}
	},

	/**
	 * Populate the value from the placeholder on input focus
	 *
	 * @param {JQuery} $collection
	 */
	'input[data-autocomplete-placeholder],textarea[data-autocomplete-placeholder]': ($collection) => {
		for (const elem of $collection) {

			/** @type {JQuery<HTMLInputElement | HTMLTextAreaElement>} */ // @ts-ignore
			const $elem = $(elem);

			// Populate the value from the placeholder on input focus and clear it on blur, if the value is the placeholder
			if ($elem.attr('placeholder')) {
				$elem.on({
					'focusin': (e) => {
						if ($elem.val() === '') {
							$elem.val($elem.prop('placeholder'));
						}
					},
					'focusout': (e) => {
						if ($elem.val() === $elem.prop('placeholder')) {
							$elem.val('');
						}
					},
				});
			}
		}
	},

	/**
	 * Ip list form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-ip-list]': ($collection) => {
		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-enabled]').on('click', (e) => {
				const $this = $(e.currentTarget);
				if ($this.prop('checked')) {
					$elem.find('[data-list-row]').removeClass('d-none').find('textarea').removeAttr('disabled');
				}
				else {
					$elem.find('[data-list-row]').addClass('d-none').find('textarea').attr('disabled', 'disabled');
				}
			});
		}
	},

	/**
	 * Range collection form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-range-collection]': ($collection) => {
		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.on('dynamic-row-added', (e, row) => {
				const $prevRow = $(row).prev();
				if ($prevRow.length > 0) {
					const prevMax = $prevRow.find('[data-max]').val();
					// @ts-ignore
					if (prevMax > 0) {
						$(row).find('[data-min]').val(prevMax);
					}
				}
			});
		}
	},

	/**
	 * Inline group collection form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-inline-group-collection]': async ($collection) => {

		const { enhanceSelects } = await import('../mpro/modules/mpro.select2.js');

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.on('dynamic-row-added', (e, row) => {
				const $row = $(row);
				if ($row.length > 0) {
					/** @type {JQuery<HTMLSelectElement>} */ // @ts-ignore
					const $customSelects = $row.find('select[data-custom-select]');
					enhanceSelects($customSelects);
				}
			});
		}
	},

	/**
	 * Select2 and numeric collection form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-primary-secondary]': async ($collection) => {

		const { enhanceSelects } = await import('../mpro/modules/mpro.select2.js');

		// Handle row actions
		const handleRow = function(row) {
			const $row = $(row);
			if ($row.length > 0) {
				/** @type {JQuery<HTMLSelectElement>} */ // @ts-ignore
				const $customSelects = $row.find('select[data-custom-select]');
				enhanceSelects($customSelects);

				const $primary = $row.find('[data-primary]');
				const $secondary = $row.find('[data-secondary]');
				const $tertiary = $row.find('[data-tertiary]');

				$primary.on('change.primary-secondary', (e, first_trigger) => {
					// Clear old values
					if (typeof first_trigger === 'undefined') {
						if ($secondary.is('select')) {
							$secondary.val($secondary.data('default') || $secondary.find('option:first').val());
						}
						else {
							$secondary.val($secondary.data('default') || '');
						}
						if ($tertiary.is('select')) {
							$tertiary.val($tertiary.data('default') || $tertiary.find('option:first').val());
						}
						else {
							$tertiary.val($tertiary.data('default') || '');
						}
					}

					// A value is selected on primary select
					// @ts-ignore
					if ($primary.val() > 0 || $primary.val() !== '') {
						$secondary.prop('disabled', false);
						$tertiary.prop('disabled', false);
					}
					else {
						$secondary.prop('disabled', true);
						$tertiary.prop('disabled', true);
					}
				})
					// Use triggerHandler to avoid anti-pattern programming
					// The 'change' event should be triggered and bubbled ONLY if it makes sense
					.triggerHandler('change.primary-secondary', [true]);
			}
		};

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-widget-dynamic-row]').each(function() {
				handleRow(this);
			});

			$elem.on('dynamic-row-added', (e, row) => {
				handleRow(row);
			});
		}
	},

	/**
	 * Net or gross form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-net-or-gross]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-format-dropdown] [data-value]').on('click', (e) => {
				e.preventDefault();

				const $link = $(e.currentTarget);

				$link.addClass('active').siblings().removeClass('active');
				$elem.find('[data-bs-toggle]').html($link.html());
				$elem.find('[data-format-selector]').val($link.data('value')).trigger('change');
			});

			$elem.find('[data-format-selector]').on('change', (e) => {
				$elem.find('[data-format-net]').toggleClass('d-none').toggleClass('last-child');
				$elem.find('[data-format-gross]').toggleClass('d-none').toggleClass('last-child');
			});
		}
	},

	/**
	 * Location selector form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-location-selector]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-country-id]').on('change', (e) => {
				const $country = $(e.currentTarget);
				$elem.find('[data-city-name]').val('');
				if ($elem.data('countries-with-nomenclature').includes($country.val())) {
					$elem.find('[data-city-id]').prop('disabled', false).removeAttr('data-disabled').closest('.city-row').removeClass('d-none');
					$elem.find('[data-city-name]').prop('disabled', true).attr('data-disabled', '').closest('.city-row').addClass('d-none');
				}
				else {
					$elem.find('[data-city-id]').prop('disabled', true).attr('data-disabled', '').closest('.city-row').addClass('d-none');
					$elem.find('[data-city-name]').prop('disabled', false).removeAttr('data-disabled').closest('.city-row').removeClass('d-none');
				}
			});
		}
	},

	/**
	 * Attribute options form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-attribute-options]': async ($collection) => {

		const { colorPickerDecorator } = await import('../mpro/modules/mpro.color-picker.js');

		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-type-selector]').on('click', (e) => {
				const $this = $(e.currentTarget);
				const value = $this.data('value');

				switch (value) {
					case 'default': {
						$elem.find('[data-color-selector]').addClass('d-none').prop('disabled', true);
						$elem.find('[data-image-selector]').addClass('d-none').prop('disabled', true);

						break;
					}
					case 'color': {
						$elem.find('[data-color-selector]').removeClass('d-none').prop('disabled', false);
						$elem.find('[data-image-selector]').addClass('d-none').prop('disabled', true);

						break;
					}
					case 'image': {
						$elem.find('[data-color-selector]').removeClass('d-none').prop('disabled', false);
						$elem.find('[data-image-selector]').removeClass('d-none').prop('disabled', false);

						break;
					}
					// No default
				}

				$elem.find('[data-type-input]').val(value);

				$this.addClass('active').siblings().removeClass('active');

			});

			$elem.find('[data-widget-dynamic]').on('dynamic-row-added', (e, row) => {
				const $row = $(row);
				const $colorPickerInputs = $row.find('[data-color-picker]');
				console.log($colorPickerInputs.length);
				if ($colorPickerInputs.length > 0) {
					colorPickerDecorator($colorPickerInputs);
				}

				const value = $elem.find('[data-type-input]').val();
				switch (value) {
					case 'default': {
						$row.find('[data-color-selector]').addClass('d-none').prop('disabled', true);
						$row.find('[data-image-selector]').addClass('d-none').prop('disabled', true);

						break;
					}
					case 'color': {
						$row.find('[data-color-selector]').removeClass('d-none').prop('disabled', false);
						$row.find('[data-image-selector]').addClass('d-none').prop('disabled', true);

						break;
					}
					case 'image': {
						$row.find('[data-color-selector]').addClass('d-none').prop('disabled', true);
						$row.find('[data-image-selector]').removeClass('d-none').prop('disabled', false);

						break;
					}
					// No default
				}

			});
		}
	},

	/**
	 * Product variants form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-product-variants]': ($collection) => {

		let hashedAttributes = '';

		const optionsNames = function($attributeOptions) {
			return $attributeOptions
				.find('option')
				.filter((i, v) => {
					return v.value > 0;
				})
				.map((i, v) => {
					return { value: Number.parseInt(v.value, 10), name: v.innerHTML };
				})
				.get()
				.reduce((all, v) => {
					all[v.value] = all[v.value] || v.name;

					return all;
				}, {});
		};

		const attributeNames = function($attributes) {
			return $attributes
				.find('option')
				.filter((i, v) => {
					return v.value > 0;
				})
				.map((i, v) => {
					return { value: Number.parseInt(v.value, 10), name: v.innerHTML };
				})
				.get()
				.reduce((all, v) => {
					all[v.value] = all[v.value] || v.name;

					return all;
				}, {});
		};

		const deletedData = [];
		const tableData = {};
		const saveTableData = function(/** @type {JQuery} */$elem) {
			const unindexed_array = $elem.find('input, select, textarea').not('[name*="][attr--"]').not('[name*="[attributes]"]').serializeArray();

			// Unset unchecked checkboxes
			$elem.find('input:checkbox, input:radio').each(function() {
				// @ts-ignore
				if (!this.checked) {
					// @ts-ignore
					tableData[this.name] = '0';
				}
			});

			$.map(unindexed_array, (n, i) => {
				if (n['value'] !== '') {
					tableData[n['name']] = n['value'];
				}
			});
		};

		// Populate values
		const populateTemplateValues = function($elem, $attributes, templates) {
			const attribute_names = attributeNames($attributes);
			const formMode = $elem.data('formMode');

			// Populate placeholders
			if (
				typeof templates === 'object' &&
				!Array.isArray(templates) &&
				templates !== null
			) {
				const escapeRegExp = function(string) {
					return string.replaceAll(/[$()*+.?[\\\]^{|}\-]/g, String.raw`\$&`);
				};

				for (const entry of Object.entries(templates)) {
					const [widget_id, template] = entry;
					/** @type {JQuery} */
					const $widgetInputs = $elem.find(`[name$="[${widget_id}]"]`);
					let placeholder = template.template;

					for (const entry of Object.entries(template['template_data'])) {
						const [replacement_id, replacement] = entry;
						const selector = replacement['selector'].split(':');

						if (selector.length === 1) {
							templates[widget_id]['template_data'][replacement_id]['value'] = $.trim(templates[widget_id]['template_data'][replacement_id]['element'].val());
						}
						else if (selector[1] === 'option_val') {
							templates[widget_id]['template_data'][replacement_id]['value'] = $.trim(templates[widget_id]['template_data'][replacement_id]['element'].find('option:selected').text());
						}
						placeholder = (templates[widget_id]['template_data'][replacement_id]['value'] !== '')
							? placeholder.replace(`{{${replacement_id}}}`, templates[widget_id]['template_data'][replacement_id]['value'])
							: placeholder.replace(new RegExp(escapeRegExp(`{{${replacement_id}}}`) + String.raw`.*?(?=\{)`), '');
					}

					const matches = template.template.match(/[._-]/g);
					let listSeparator = '_';
					if (matches.length > 0) {
						listSeparator = matches[0];
					}

					$widgetInputs.each(function() {
						const $this = $(this);
						let variantPlaceholder = placeholder;
						const variantReplacements = $this.closest('tr').find('input[name*="[attr--"]').map(function() {
							// @ts-ignore
							const nameFragments = this.name.split('][');
							return {
								'id': nameFragments.at(-2).split('--')[1],
								'name': attribute_names[nameFragments.at(-2).split('--')[1]],
								'value': $(this).closest('td').text()?.trim(),
								// @ts-ignore
								'value_id': this.value,
							};
						}).toArray();

						variantPlaceholder = variantPlaceholder.replace('{{PROPERTY.ids_list}}', variantReplacements.map(v => v.value_id).join(listSeparator));
						variantPlaceholder = variantPlaceholder.replace('{{PROPERTY.values_list}}', variantReplacements.map(v => v.value).join(listSeparator));

						for (let i = 0; i < 5; i++) {
							if (typeof variantReplacements[i] !== 'undefined') {
								variantPlaceholder = variantPlaceholder.replace(`{{PROPERTY[${i + 1}].id}}`, variantReplacements[i].id);
								variantPlaceholder = variantPlaceholder.replace(`{{PROPERTY[${i + 1}].name}}`, variantReplacements[i].name);
								variantPlaceholder = variantPlaceholder.replace(`{{PROPERTY[${i + 1}].value}}`, variantReplacements[i].value);
								variantPlaceholder = variantPlaceholder.replace(`{{PROPERTY[${i + 1}].value_id}}`, variantReplacements[i].value_id);
							}
							else {
								variantPlaceholder = variantPlaceholder.replace(new RegExp(escapeRegExp(`{{PROPERTY[${i + 1}].id}}`) + String.raw`.*?(?=\{)`), '');
								variantPlaceholder = variantPlaceholder.replace(new RegExp(escapeRegExp(`{{PROPERTY[${i + 1}].name}}`) + String.raw`.*?(?=\{)`), '');
								variantPlaceholder = variantPlaceholder.replace(new RegExp(escapeRegExp(`{{PROPERTY[${i + 1}].value}}`) + String.raw`.*?(?=\{)`), '');
								variantPlaceholder = variantPlaceholder.replace(new RegExp(escapeRegExp(`{{PROPERTY[${i + 1}].value_id}}`) + String.raw`.*?(?=\{)`), '');
							}
						}

						// Cleanup
						variantPlaceholder = variantPlaceholder.replaceAll(/  +/g, ' ');
						variantPlaceholder = variantPlaceholder.replace(new RegExp(`[${escapeRegExp('\`~!@#$%^&*-_=+,.?/\\|')}]*$`), '');

						if ((formMode === 'add') || (formMode === 'edit' && String($this.val()).trim().length === 0)) {
							$this.val(variantPlaceholder);
						}
					});
				}
			}
		};

		// Rebuild table
		const rebuildTable = function($elem, $attributes, $attributeOptions, templates) {

			const cartesian = (...a) => a.reduce((a, b) => a.flatMap(d => b.map(e => [d, e].flat())));

			// Get attributes and attribute values and combine them into a single object ================
			const attribute_values = $attributes.serializeArray().reduce((all, v) => {
				if (v.value !== '') {
					const index = Number.parseInt(v.name.match(/\[fields]\[(\d)+]/)[1], 10);
					all[index] = all[index] || [];
					all[index].push(Number.parseInt(v.value, 10));
				}

				return all;
			}, {});

			const attribute_names = attributeNames($attributes);

			const options_values = $attributeOptions.serializeArray().reduce((all, v) => {
				const index = Number.parseInt(v.name.match(/\[fields]\[(\d)+]/)[1], 10);
				all[index] = all[index] || [];
				all[index].push(Number.parseInt(v.value, 10));

				return all;
			}, {});

			const options_names = optionsNames($attributeOptions);

			const combined_values = {};
			for (const entry of Object.entries(attribute_values)) {
				const [key, value] = entry;
				if (typeof options_values[key] !== 'undefined') {
					// Concat with diff
					combined_values['attr--' + value[0]] = (combined_values['attr--' + value[0]] || []).concat(options_values[key].filter(x => !(combined_values['attr--' + value[0]] || []).includes(x)));
				}
			}
			const filtered_attribute_values = Object.fromEntries(Object.entries(attribute_values).filter(([key, value]) => typeof options_values[key] !== 'undefined'));

			// =========================================================================================
			if (hashedAttributes !== JSON.stringify(combined_values)) {
				hashedAttributes = JSON.stringify(combined_values);

				const templates = $elem.data('templates');

				saveTableData($elem);

				let headerHtml = '';
				let html = '';

				if (Object.keys(combined_values).length > 0) {
					// Rebuild table header
					for (const entry of Object.entries(attribute_values)) {
						const [key, value] = entry;
						if (typeof options_values[key] !== 'undefined') {
							headerHtml += '<th class="attribute">' + attribute_names[value[0]] + '</th>';
						}
					}

					// Rebuild table
					const maxVariants = Number.parseInt($elem.data('maxVariants')) || null;
					const prototype = '<table>' + $elem.data('prototype') + '</table>';
					const $prototype = $(prototype).find('input[id*="_attr--"]').closest('td').remove().end().end();
					const cellPrototype = '<div>' + $elem.data('cellPrototype') + '</div>';
					const colors = (typeof $elem.data('colors') !== 'undefined' ? $elem.data('colors') : []);

					const variantData = cartesian(...Object.values(combined_values));

					// Populate default data
					$prototype.find('[data-default]').each(function() {
						const $this = $(this);
						if ($this.is(':checkbox') || $this.is(':radio')) {
							$this.attr('checked', $this.data('default'));
						}
						else {
							$this.attr('value', $this.data('default'));
						}
					});

					for (let options of variantData) {

						if (!(Array.isArray(options))) {
							options = [options];
						}

						const options_ids = [...options].sort((a, b) => {
							return a - b;
						}).join('_');

						// Skip deleted variants
						if (deletedData.includes(options_ids)) {
							continue;
						}

						let rowHtml = ''; let cnt = 0;
						for (const optionValue of options) {
							let index = 0;
							for (const entry of Object.entries(options_values)) {
								const [key, value] = entry;
								if ((value || []).includes(optionValue)) {
									index = Number(key);
								}
							}

							rowHtml += '<td>' + cellPrototype
								.replaceAll('__option_value__', optionValue)
								.replaceAll('__option_name__', options_names[optionValue])
								.replaceAll('__option_color__', 'attribute-color-' + colors[index])
								.replaceAll('__variant_id__', options_ids)
								.replaceAll('__attribute_name__', 'attr--' + attribute_values[Object.keys(filtered_attribute_values)[cnt++]]) + '</td>';
						}

						html += $prototype.clone().find('td:first').before(rowHtml).end().find('> tbody').html().replaceAll('__name__', options_ids);
					}

					const message = `<div class="d-flex nowrap justify-content-between">
						<div>${$elem.data('countVariantsMessage').replaceAll('__count__', variantData.length)}</div>
						<div>${$elem.data('maxVariantsMessage').replaceAll('__limit__', $elem.data('maxVariants'))}</div>
					</div>`;

					$elem.find('[data-count-variants-message-container]').html(message);
				}

				$elem.find('tr[data-widget-header]').find('th.attribute').remove().end().find('th:first').before(headerHtml);
				$elem.find('tr[data-widget-header]').nextAll('tr').remove().end();
				$elem.find('tbody').html(html);
				$elem.find('[data-widget-head]').removeClass('d-none');
				$elem.find('[data-show-max-variants-message]').addClass('d-none');

				$elem.find('[data-count-variants-message-container]').attr('colspan', $elem.find('[data-widget-header] > th').length);

				for (const entry of Object.entries(tableData)) {
					const [key, value] = entry;
					const $input = $elem.find('[name="' + key + '"]');
					if ($input.is(':checkbox') || $input.is(':radio')) {
						$input.prop('checked', Boolean(Number(value)));
					}
					else {
						$elem.find('[name="' + key + '"]').val(value);
					}
				}

				populateTemplateValues($elem, $attributes, templates);

				const $bulkEditTables = $elem.find('[data-bulk-edit-table]');
				if ($bulkEditTables.length > 0) {
					import('../mpro/modules/mpro.bulk-edit-table.js').then(({ enhanceBulkTables }) => enhanceBulkTables($bulkEditTables));
				}

				// Re-init content plugins
				mpro.initStatics($elem);
			}
		};

		for (const elem of $collection) {
			const $elem = $(elem);
			const $attributesWidget = $elem.find('[data-widget-dual-select2]').first();

			// Get autocomplete templates
			const templates = $elem.data('templates');
			let templatesTriggers = $([]);
			if (
				typeof templates === 'object' &&
				!Array.isArray(templates) &&
				templates !== null
			) {
				for (const entry of Object.entries(templates)) {
					const [widget_id, template] = entry;
					for (const entry of Object.entries(template['template_data'])) {
						const [replacement_id, replacement] = entry;
						const selector = replacement['selector'].split(':');
						const $templateElement = mpro.get$B().find(`[name="${selector[0]}"]`);
						if ($templateElement.length > 0) {
							templates[widget_id]['template_data'][replacement_id]['element'] = $templateElement;
							templatesTriggers = templatesTriggers.add($templateElement);
						}
					}
				}

				const $attributes = $attributesWidget.find('.select2-hidden-accessible[name*="[attribute]"]');
				populateTemplateValues($elem, $attributes, templates);

				templatesTriggers.on('change blur', (e) => {
					populateTemplateValues($elem, $attributes, templates);
				});
			}

			// Sync stock inputs
			$elem.on('change', '.hide-infinite-stock input', (e) => {
				const $this = $(e.currentTarget);
				const $tr = $this.closest('tr');
				const $stock = $this.closest('.hide-infinite-stock');
				if ($stock.is('.show-infinite-stock')) {
					$tr.find('.hide-infinite-stock').not($stock).addClass('show-infinite-stock');
					$tr.find('.hide-infinite-stock').not($stock).find('[data-toggle-stock]').each(function() {
						this.dataset.toggleStock = 'true';
					});
					// @ts-ignore
					$tr.find('.hide-infinite-stock').not($stock).find('input:hidden').each(function() {
						// @ts-ignore
						this.disabled = false;
					});
				}
				else {
					$tr.find('.hide-infinite-stock').not($stock).removeClass('show-infinite-stock');
					$tr.find('.hide-infinite-stock').not($stock).find('[data-toggle-stock]').each(function() {
						this.dataset.toggleStock = 'false';
					});
					// @ts-ignore
					$tr.find('.hide-infinite-stock').not($stock).find('input:hidden').each(function() {
						// @ts-ignore
						this.disabled = true;
					});
				}
			});

			// Add / remove column in table
			$attributesWidget.on('change dynamic-row-after-remove onUpdate', (e) => {
				const $attributes = $attributesWidget.find('.select2-hidden-accessible[name*="[attribute]"]');
				const $attributeOptions = $attributesWidget.find('.select2-hidden-accessible[name*="[attribute_options]"]');
				if (($attributes.index(e.target) !== -1) || ($attributeOptions.index(e.target) !== -1) || ($attributesWidget.is(e.currentTarget))) {
					// Add loading animation over the preview item
					// mpro.addContentLoader($elem.parent(), ['bg-75', 'lg']);
					// mpro.addLoading($elem);
					// $elem.addClass('loading');
					if (e.type !== 'change' || typeof e.originalEvent === 'undefined') {
						rebuildTable($elem, $attributes, $attributeOptions, templates);
					}

					// Toggle row class
					$elem.find('input[data-toggle-row-class]').each(function() {
						const $this = $(this);
						const dataToggle = $this.data('toggleRowClass');
						// @ts-ignore
						$this.closest('tr')[0].className = dataToggle[Number(this.checked)];
					});

					// Remove loading animation from the preview item
					mpro.removeContentLoader($elem);
				}

				// $elem.trigger('change');
			});

			$elem.on('dynamic-row-before-remove', (e, $row) => {
				deletedData.push(String($row.data('key')));
			});

			$elem.on('dynamic-row-after-remove', (e) => {
				const maxVariants = String(Number.parseInt($elem.data('maxVariants'), 10) || '');
				const countVariants = $elem.find('table[data-bulk-edit-table] > tbody > tr').length;

				const message = `<div class="d-flex nowrap justify-content-between">
					<div>${$elem.data('countVariantsMessage').replaceAll('__count__', countVariants)}</div>
					<div>${$elem.data('maxVariantsMessage').replaceAll('__limit__', maxVariants)}</div>
				</div>`;

				$elem.find('[data-count-variants-message-container]').html(message);
			});

			// Toggle row class
			$elem.find('input[data-toggle-row-class]').each(function() {
				const $this = $(this);
				const dataToggle = $this.data('toggleRowClass');
				// @ts-ignore
				$this.closest('tr')[0].className = dataToggle[Number(this.checked)];
			});
			$elem.on('click', 'input[data-toggle-row-class]', function(e) {
				const $this = $(this);
				const dataToggle = $this.data('toggleRowClass');
				$this.closest('tr')[0].className = dataToggle[Number(this.checked)];
			});

			// Autofill
			$elem.on('click', 'tr[data-widget-header] [data-autofill]', (e) => {
				e.stopImmediatePropagation();
				e.stopPropagation();

				const $this = $(e.currentTarget);
				const $th = $this.closest('th');
				const index = $this.closest('tr').find('> th').index($th);

				const $inputs = $elem.find('tbody > tr > td:nth-child(' + (index + 1) + ')').find('input,textarea,select');
				// @ts-ignore
				const $firstValue = $inputs.filter(function() {
					// @ts-ignore
					return $.trim($(this).val()) !== '';
				}).first();

				if ($firstValue.length > 0) {
					$inputs.each(function() {
						const $this = $(this);
						// @ts-ignore
						if ($.trim($this.val()) === '') {
							$this.val($firstValue.val());
						}
					});
				}
			});
		}
	},

	/**
	 * Product attributes form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-product-attributes]': ($collection) => {

		let hashedAttributes = '';

		const optionsNames = ($attributeOptions) => {
			return $attributeOptions
				.find('option')
				.filter((i, v) => {
					return v.value > 0;
				})
				.map((i, v) => {
					return { value: Number.parseInt(v.value, 10), name: v.innerHTML };
				})
				.get()
				.reduce((all, v) => {
					all[v.value] = all[v.value] || v.name;

					return all;
				}, {});
		};

		const attributeNames = ($attributes) => {
			return $attributes
				.find('option')
				.filter((i, v) => {
					return v.value > 0;
				})
				.map((i, v) => {
					return { value: Number.parseInt(v.value, 10), name: v.innerHTML };
				})
				.get()
				.reduce((all, v) => {
					all[v.value] = all[v.value] || v.name;

					return all;
				}, {});
		};

		const tableData = {};
		const saveTableData = function($elem) {
			const unindexed_array = $elem.find('input, select, textarea').not('[name*="[attributes]"]').serializeArray();

			$.map(unindexed_array, (n, i) => {
				if (n['value'] !== '') {
					tableData[n['name']] = n['value'];
				}
			});
		};

		// Rebuild table
		const rebuildTable = function($elem, $attributes, $attributeOptions) {

			// Get attributes and attribute values and combine them into a single object ================
			const attribute_values = $attributes.serializeArray().reduce((all, v) => {
				if (v.value !== '') {
					const index = Number.parseInt(v.name.match(/\[fields]\[(\d)+]/)[1], 10);
					all[index] = all[index] || [];
					all[index].push(Number.parseInt(v.value, 10));
				}

				return all;
			}, {});

			const attribute_names = attributeNames($attributes);
			const options_values = $attributeOptions.serializeArray().reduce((all, v) => {
				const index = Number.parseInt(v.name.match(/\[fields]\[(\d)+]/)[1], 10);
				all[index] = all[index] || [];
				all[index].push(Number.parseInt(v.value, 10));

				return all;
			}, {});

			const options_names = optionsNames($attributeOptions);

			const combined_values = {};
			for (const entry of Object.entries(attribute_values)) {
				const [key, value] = entry;
				if (typeof options_values[key] !== 'undefined') {
					// Concat with diff
					combined_values['attr--' + value[0]] = (combined_values['attr--' + value[0]] || []).concat(options_values[key].filter(x => !(combined_values['attr--' + value[0]] || []).includes(x)));
				}
			}

			// =========================================================================================

			if (hashedAttributes !== JSON.stringify(combined_values)) {
				hashedAttributes = JSON.stringify(combined_values);

				saveTableData($elem);

				let html = '';

				if (Object.keys(combined_values).length > 0) {

					// Rebuild table
					const colors = (typeof $elem.data('colors') !== 'undefined' ? $elem.data('colors') : []);
					const prototype = '<table>' + $elem.data('prototype') + '</table>';

					for (const optionValue of Object.values(combined_values).flat()) {
						const $prototype = $(prototype).clone();

						let index = 0;
						for (const entry of Object.entries(options_values)) {
							const [key, value] = entry;
							if ((value || []).includes(optionValue)) {
								index = Number(key);
							}
						}
						$prototype.find('input[name$="][option_name]"]')
							.attr('value', options_names[optionValue])
							.removeClass('attribute-color-' + colors[0])
							.addClass('attribute-color-' + colors[index]);
						if ($prototype.find('> tbody').length > 0) {
							html += $prototype.find('> tbody').html().replaceAll('__name__', optionValue);
						}
					}
				}

				$elem.find('tr[data-widget-header]').nextAll('tr').remove().end();
				$elem.find('tbody').html(html);

				for (const entry of Object.entries(tableData)) {
					const [key, value] = entry;
					$elem.find('[name="' + key + '"]').val(value);
				}
			}
		};

		for (const elem of $collection) {
			const $elem = $(elem);
			const $attributesWidget = $elem.find('[data-widget-dual-select2]').first();

			$elem.find('[data-allow-price]').on('click', (e) => {
				const $this = $(e.currentTarget);
				if ($this.prop('checked')) {
					$elem.find('tbody,[data-widget-header]').removeClass('d-none');
					const $attributes = $attributesWidget.find('.select2-hidden-accessible[name*="[attribute]"]');
					const $attributeOptions = $attributesWidget.find('.select2-hidden-accessible[name*="[attribute_options]"]');
					rebuildTable($elem, $attributes, $attributeOptions);
				}
				else {
					$elem.find('tbody,[data-widget-header]').addClass('d-none');
				}
			});

			// Add / remove column in table
			$attributesWidget.on('change dynamic-row-after-remove', (e) => {
				const $attributes = $attributesWidget.find('.select2-hidden-accessible[name*="[attribute]"]');
				const $attributeOptions = $attributesWidget.find('.select2-hidden-accessible[name*="[attribute_options]"]');
				if (($attributes.index(e.target) !== -1) || ($attributeOptions.index(e.target) !== -1) || ($attributesWidget.is(e.currentTarget))) {
					rebuildTable($elem, $attributes, $attributeOptions);
				}

				const selectionRequired = $elem.find('[data-widget-selection-required]');
				if ($attributeOptions.map(function() {
					return $(this).val();
				}).length > 0) {
					selectionRequired.prop('disabled', false);
				}
				else {
					selectionRequired.prop('disabled', true);
				}
			});
		}
	},

	/**
	 * Order line items form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-order-line-items]': ($collection) => {
		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.on('dynamic-row-before-remove', (e, $row) => {
				const $input = $row.find('[name$="[qty]"]');
				if ($input.data('ajax-submit')) {
					e.preventDefault();
					$input.val(0).trigger('change');
				}
			});
		}
	},

	/**
	 * Simple toggle
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-toggle]': ($collection) => {
		for (const elem of $collection) {
			const $elem = $(elem);
			const $target = $($elem.data('widgetToggle'));

			if ($target.length > 0) {
				$elem.on('click', () => {
					$target.toggle();
				});
			}
		}
	},

	/**
	 * Substatus form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-substatus]': ($collection) => {
		for (const elem of $collection) {
			const $elem = $(elem);

			$elem.find('[data-status]').on('change', (e) => {
				$elem.find('[data-substatus-row] select,[data-substatus-row] input').prop('disabled', true).attr('data-disabled', '');
				$elem.find('[data-substatus-row]').addClass('d-none');

				const $status = $(e.currentTarget);
				if ($status.val() && $status.prop('checked')) {
					const $substatus = $elem.find('[data-status-id=' + $status.val() + ']');
					const $substatusRow = $substatus.closest('[data-substatus-row]');

					$substatusRow.removeClass('d-none');
					$substatusRow.find('input, select').prop('disabled', false).removeAttr('data-disabled');
				}
			});
		}
	},

	/**
	 * Fields mapping form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-fields-mapping]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			const $select = $elem.find('select').eq(0);
			const $hiddens = $elem.find('input[type=hidden]');
			let page = 0;

			$elem.find('[data-page-next],[data-page-prev]').on('click', (e) => {
				e.preventDefault();
				const max = ($elem.find('[data-pages]').first().data('pages').length - 1);
				if ($(e.currentTarget).is('[data-page-next]')) {
					if (page === max) {
						return;
					}
					page++;
				}
				else {
					if (page === 0) {
						return;
					}
					page--;
				}
				if (page <= 0) {
					$elem.find('[data-page-prev]').addClass('disabled');
				}
				else {
					$elem.find('[data-page-prev]').removeClass('disabled');
				}
				if (page >= max) {
					$elem.find('[data-page-next]').addClass('disabled');
				}
				else {
					$elem.find('[data-page-next]').removeClass('disabled');
				}
				$elem.find('[data-page-current]').html(String(page + 1));

				$elem.find('[data-pages]').each(function() {
					const data = $(this).data('pages');
					$(this).find('[data-content]').html(data[page]);
				});
			});

			$hiddens.each(function() {
				const $this = $(this);
				const val = $this.val();
				$this.replaceWith($select.clone().val(val).attr('id', $this.attr('id')).attr('name', $this.attr('name')));
			});
		}
	},

	/**
	 * Dynamic filter form type
	 *
	 * @param {JQuery} $collection
	 */
	'[data-widget-dynamic-add]': ($collection) => {

		for (const elem of $collection) {
			const $elem = $(elem);
			$elem.off('click.dynamic_add').on('click.dynamic_add', (e) => {

				const $collectionHolder = $elem.prevAll('[data-widget-dynamic]').last();
				if ($collectionHolder.is('[data-disabled]')) {
					return;
				}

				if ((typeof $collectionHolder.data('max') !== 'undefined') && ($collectionHolder.find('[data-widget-dynamic-row]').length >= $collectionHolder.data('max'))) {
					if (typeof $collectionHolder.data('maxErrorMessage') !== 'undefined') {
						mpro.toast('error', $collectionHolder.data('maxErrorMessage'));
					}
					return;
				}

				const $collectionContainer = $collectionHolder.find('[data-widget-dynamic-container]');
				/** @type {HTMLElement} */ // @ts-ignore
				const collectionHolder = $collectionHolder[0];
				const collectionContainer = $collectionContainer[0];

				// Make sure the index exists and is integer
				// @ts-ignore
				collectionHolder.dataset.index = Number(collectionHolder.dataset.index || 0);

				let $item = $(collectionHolder.dataset.prototype.replaceAll('__name__', String(collectionHolder.dataset.index)));
				// Prevent duplicate ids
				while ($collectionHolder.find('#' + $item[0].id).length > 0) {
					// Increment collection widget index
					// @ts-ignore
					collectionHolder.dataset.index++;
					$item = $(collectionHolder.dataset.prototype.replaceAll('__name__', String(collectionHolder.dataset.index)));
				}
				/** @type {HTMLElement} */ // @ts-ignore
				let item;
				if ($collectionHolder.is('[data-disabled]')) {
					$item.find(':input').prop('disabled', true);
				}
				else {
					$item.find(':input').prop('disabled', false);
				}

				// Duplicate widget with proper index
				if ($collectionHolder.is('table')) {
					item = $item[0];
					if ($collectionContainer.length > 0) {
						$collectionContainer.children('tbody').append(item);
						collectionContainer.scrollTop = collectionContainer.scrollHeight;
					}
					else {
						$collectionHolder.children('tbody').append(item);
						collectionHolder.scrollTop = collectionHolder.scrollHeight;
					}
				}
				else {
					item = document.createElement('div');
					item.classList.add('form-group');
					item.innerHTML = $item.wrapAll('<div></div>').parent().html();

					// Add widget to collection
					if ($collectionContainer.length > 0) {
						collectionContainer.append(item);
						collectionContainer.scrollTop = collectionContainer.scrollHeight;
					}
					else {
						collectionHolder.append(item);
						collectionHolder.scrollTop = collectionHolder.scrollHeight;
					}
				}

				// Increment collection widget index
				// @ts-ignore
				collectionHolder.dataset.index++;

				// Trigger row add event
				$(collectionHolder).closest('[data-widget-dynamic]').triggerHandler('dynamic-row-added', [item]);
			});
		}
	},

	/**
	 * Remove empty elements from the form, right before the submit event
	 *
	 * @param {JQuery<HTMLFormElement>} $collection
	 */
	'[data-filter-submit]': ($collection) => {

		for (const form of $collection) {

			/** @type {JQuery<HTMLFormElement>} */ // @ts-ignore
			const $form = $(form);

			$form.on('submit.filter-submit', (e) => {

				// Get submit data from the form
				const formData = new FormData(form);
				let $alteredFields = $();

				for (const [name, value] of formData) {
					// Remove the names of elements with empty values
					if (value === '' || typeof $form.find(`[name="${name}"]`).data('skip') !== 'undefined') {
						$alteredFields = $alteredFields.add($form.find(`[name="${name}"]`).attr('data-original-field-name', name).removeAttr('name'));
					}
				}

				// Restore original form names
				setTimeout(() => {
					for (const elem of $alteredFields) {
						const $elem = $(elem);
						$elem.attr('name', $elem.attr('data-original-field-name')).removeAttr('data-original-field-name');
					}
				});
			});
		}
	},

	/**
	 * Files manager expand
	 *
	 * @param {JQuery<HTMLFormElement>} $collection
	 */
	'.files-manager .menu-folder-has-children > i, .files-manager .menu-folder-has-children > .content-toggle': ($collection) => {

		for (const menuItem of $collection) {

			const $menuItem = $(menuItem);
			const $parent = $menuItem.parent();
			const $arrow = $parent.find('[class^="mdi mdi-chevron-"]');
			const $folder = $parent.find('[class^="mdi mdi-folder-"]');

			$menuItem.on('click', (e) => {

				// Prevent accessing the link
				e.stopImmediatePropagation();
				e.preventDefault();

				// Open the children element
				$parent.toggleClass('menu-folder-opened');
				// Toggle arrows states
				$arrow.toggleClass('mdi-chevron-up mdi-chevron-right');
				$folder.toggleClass('mdi-folder-outline mdi-folder-open-outline');
			});
		}
	},

	/**
	 * Templates manager expand
	 *
	 * @param {JQuery<HTMLFormElement>} $collection
	 */
	'.templates-editor-column': ($collection) => {

		$collection.on('click', '.menu-folder-has-children > i, .menu-folder-has-children > .content-toggle', (e) => {
			// Prevent accessing the link
			e.stopImmediatePropagation();
			e.preventDefault();

			$(e.currentTarget).parent().toggleClass('menu-folder-opened');
		});

		// Attach a 'keyup' event handler to the input field with the class '.form-filter' within $collection
		$collection.on('keyup', '.form-filter', (e) => {
			const $input = $(e.currentTarget); // Get the input element that triggered the event
			const $container = $input.closest('.templates-editor-column'); // Find the closest '.templates-editor-column' container
			const searchText = $input.val().trim(); // Get and trim the input value (search text)

			// If the input has text, perform the filtering
			if (searchText.length > 0) {
				// Hide all menu folders and files, and remove the expanded state from folders
				$container.find('.menu-folder, .menu-file').addClass('d-none').removeClass('menu-folder-expanded');
				// .removeClass('menu-folder-opened');

				// Reset the text of each '.content-toggle' and 'a' element inside the folders and files
				$container.find('.menu-folder .content-toggle, .menu-file a').each(function() {
					const $element = $(this);
					// Restore the original text if it was stored in data attribute, or keep the current text
					$element.html($element.data('original-text') || $element.text());
				})
					// Filter the elements based on whether their text includes the search text or if they are active files
					.filter(function() {
						return $(this).text().toLowerCase().includes(searchText);
						// || $(this).parent().hasClass('menu-file-active');
					})
					// Highlight the matched text in the filtered elements
					.each(function() {
						const $element = $(this);
						const regex = new RegExp('(' + searchText + ')', 'gi'); // Create a case-insensitive regex for the search term
						const originalText = $element.data('original-text') || $element.text(); // Get or store the original text
						const highlightedText = originalText.replace(regex, '<span class="text-highlight">$1</span>'); // Wrap matched text with highlight span
						$element.data('original-text', originalText).html(highlightedText); // Update element with highlighted text and store the original text
					})
					// Show the filtered elements and expand their parent folders
					.parent().removeClass('d-none').parents('li').find('> .menu-folder').removeClass('d-none').addClass('menu-folder-expanded');
			}
			// If the input is empty, reset the view to show all elements
			else {
				// Show all folders and files, and remove the expanded state from folders
				$container.find('.menu-folder, .menu-file').removeClass('d-none').removeClass('menu-folder-expanded');

				// Reset the text of all '.content-toggle' and 'a' elements to their original state
				$container.find('.menu-folder .content-toggle, .menu-file a').each(function() {
					const $element = $(this);
					$element.html($element.data('original-text') || $element.text()); // Restore original text if stored, otherwise keep current text
				});
			}
		});
	},

	/**
	 * Auto-resize iframe
	 *
	 * @param {JQuery<HTMLIFrameElement>} $collection
	 */
	'iframe[data-auto-resize]': ($collection) => {

		// TODO: optimize this
		for (const iframe of $collection) {

			// Resize iframe on window resize
			$(iframe.contentWindow).on('resize', () => {
				resizeIframeToInnerContent(iframe);
			});

			$(iframe).on('load', () => {
				resizeIframeToInnerContent(iframe);
			});

			resizeIframeToInnerContent(iframe);
		}
	},

	/**
	 * Remote action handling
	 *
	 * @param {JQuery} $collection
	 */
	// TODO: move to a separate file
	// TODO: add support for multiple selectors
	'a[data-mp-remote-action],button[data-mp-remote-action]': ($collection) => {

		// TODO: optimize this
		for (const element of $collection) {

			const $element = $(element);

			$element.off('click.mp-remote-action').on('click.mp-remote-action', async (e) => {

				/** @type {HTMLAnchorElement & HTMLButtonElement} */ // @ts-ignore
				// Use the element that triggered the event (might be a child of the actual element)
				const actionButton = e.delegateTarget;
				const $actionButton = $(actionButton);

				const options = { ...$actionButton.data() };
				const mpRemoteAction = options.mpRemoteAction || {};
				const urlCache = getElementUrl(actionButton);
				const { url } = urlCache;

				// ? Not really the best idea to merge the togglePrefs action with the remote-action
				const showLoader = (mpRemoteAction.loader !== false);
				const togglePrefs = mpRemoteAction.togglePrefs ? { ...mpRemoteAction.togglePrefs } : false;
				// This context is only used for the extra loader
				const { context } = mpRemoteAction;
				const $context = context ? (context === 'parent' ? $actionButton.parent() : $(context)) : null;

				if (togglePrefs !== false) {
					const {
						value: prefValue,
						labelSelector,
						valueSelector,
					} = togglePrefs;

					const $labelElement = labelSelector ? $(labelSelector) : $actionButton.closest('.dropdown').find('.action-link, .btn').first();
					const $valueElement = valueSelector ? $(valueSelector) : $actionButton.closest('.dropdown').find('.action-link, .btn').first();
					const valueElement = $valueElement[0];

					let $targetText = $labelElement.find('.action__text');
					if ($targetText.length === 0) {
						$targetText = $labelElement;
					}
					let $triggerText = $actionButton.find('.action__text');
					if ($triggerText.length === 0) {
						$triggerText = $actionButton;
					}
					$targetText.text($triggerText.text());

					if ($valueElement.is('button,input')) {
						$valueElement.prop('value', prefValue);
						// data-href button
						if ($valueElement.is('[data-href]')) {
							setElementUrl(valueElement, url);
						}
					}
					else if (valueElement) {
						valueElement.dataset.value = prefValue;
						setElementUrl(valueElement, url);
					}

					$actionButton.closest('.dropdown-menu').find('.active').removeClass('active');
					$actionButton.addClass('active');
				}

				// Not a valid action
				if (!url || url.startsWith('#') || url.startsWith('javascript:')) {
					return;
				}

				// Show a loading animation on the element
				if (showLoader) {
					if ($context) {
						mpro.addContentLoader($context, 'auto');
					}
					if (!$context || $actionButton.closest($context).length === 0) {
						mpro.addButtonLoading($actionButton);
					}
				}
				delete options.mpRemoteAction;
				delete options.mpRemoteActionUrl;

				// Final event
				e.preventDefault();

				// When toggling the preferences, we want to propagate the click event so that the dropdown closes
				if (togglePrefs === false) {
					e.stopImmediatePropagation();
					e.stopPropagation();
				}

				try {

					// Data will be altered if dataFrom is set
					let { data, dataFrom, method = 'GET' } = mpRemoteAction;

					// Get data from another element
					if (dataFrom) {

						// Send form data
						if (dataFrom === 'form') {
							// @ts-expect-error: Argument of type 'JQuery<HTMLElement>' is not assignable to parameter of type 'HTMLFormElement'
							data = new FormData(($context || $actionButton).closest('form')[0]);
						}
						// Send only some keys
						else if (isPlainObject(dataFrom)) {

							// Reset the data object
							data = new FormData();

							for (const [name, selector] of Object.entries(dataFrom)) {
								getFormDataForElement(selector, name, data);
							}
						}
						// Replace the whole data object
						else {
							/** @type {JQuery} */ // @ts-ignore
							const $element = $(dataFrom);
							data = getFormDataForElement($element);
						}
					}

					const response = await doFetch(url, {
						method,
						data,
						...options,
						headers: {
							'X-Remote-Signature': 'remote-action',
						},
					}, {
						// To provide access to AbortController.abort() method through ref.abort()
						ref: null,
					});

					// Check if the response is a partial content
					if (response?.content) {

						let message = 'RemoteAction error: the response is a partial content, but the remote-action handler doesn\'t handle content.';

						// This seems like standard table row response
						if (response.content?.startsWith?.('<tr')) {
							message += '\r\nDid you perhaps mean to use the Datatable "[data-mp-dt-remote-action]" handler? To use that handler, make sure you render the block with ["render_mode" => "datatable" or "datatable-section" or "dropdown-item-datatable"] option with whatever rendering method you used.';
						}

						// This type of remote-request doesn't handle content, inform the user
						displayEnvActionAlert(new Error(message), {
							action: {
								type: 'remote-action',
								url,
								method: options.method || 'GET',
							},
						});
					}
					// Empty content - reproduce the loadPartial behavior to remove the content
					else if (response?.content === '') {
						($context || $actionButton).remove();
					}

					// Handle the response in a generic way
					handleRemoteResponse(new PartialContent({
						url,
						response,
						$context: $actionButton,
						$relatedTarget: $actionButton,
					}));
				}
				catch (error) {

					// Log the error details
					console.error('RemoteAction error:', error);

					// Display the error message in a bigger toast, if on dev env
					displayEnvActionAlert(error, {
						action: {
							type: 'remote-action',
							url,
							method: options.method || 'GET',
						},
					});
				}
				finally {
					// Remove the loading animation
					if (showLoader) {
						mpro.removeButtonLoading($actionButton);
						if ($context) {
							mpro.removeContentLoader($context);
						}
					}
				}
			});
		}
	},

	/**
	 * Clear files on history back
	 *
	 * @param {JQuery} $collection
	 */
	// 'form[autocomplete="off"],input[autocomplete="off"],select[autocomplete="off"],textarea[autocomplete="off"]': ($collection) => {

	// 	for (const elem of $collection) {

	// 		/** @type {JQuery<HTMLFormElement|HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>} */ // @ts-ignore
	// 		const $elem = $(elem);

	// 		mpro.oncePageRestore(() => {
	// 			if (elem instanceof HTMLFormElement) {
	// 				elem.reset();
	// 			}
	// 			else {
	// 				$elem.val(null);
	// 			}
	// 		});
	// 	}
	// },

	/**
	 * Plugin: Tick/Flip counter
	 *
	 * @param {JQuery} $collection
	 */
	// '.tick[data-tick-counter]': async ($collection) => {

	// 	// Import the tick plugin to handle counters
	// 	const { 'default': Tick } = await import('../modules/tick');

	// 	for (const elem of $collection) {
	// 		Tick.DOM.create(elem);
	// 	}
	// },

	/**
	 * Plugin: mp-counter (lazy)
	 *
	 * @param {JQuery} $collection
	 */
	// 'mp-counter': async ($collection) => {

	// 	// Just import the custom element implementation, nothing else is needed
	// 	await import('../modules/counter');

	// 	for (const elem of $collection) {
	// 		console.log('mpCounter: %o', elem);
	// 	}
	// },
};

// Add init function that is executed on every page/fragment render
mpro.addInitStatic(($container) => {

	// Init plugins
	// TODO: make all plugin handlers async to prevent page hanging
	for (const [selector, handler] of Object.entries(pluginSelectors)) {

		/** @type {JQuery<any>} */
		const $collection = $container.find(selector);

		// Apply the handler to the collection
		if ($collection.length > 0) {
			try {
				handler($collection, selector);
			}
			catch (error) {
				console.warn('Plugin handler (%o) error:', selector, error);
			}
		}
	}
});

// Add init function that is executed only on first dom-ready (it should contain only document/body delegate handlers)
mpro.addInitDelegate(async ($) => {

	// var $W = mpro.get$W();
	const $D = mpro.get$D();
	const $B = mpro.get$B();

	// Remove the 'js-not-ready' class from the document
	document.documentElement.classList.remove('js-not-ready');

	// Add browser/os classes
	const ua = mpro.explainUa();
	// Add touch / no-touch class on body
	$D.addClass(mpro.isTouchDevice() ? 'touch' : 'no-touch' + ` browser-${ua.browser} platform-${ua.os}`);

	// Disable hash for #0
	$B.on('click', 'a[href="#0"]', (e) => {
		e.preventDefault();
	});
	// Stop propagation
	$B.on('click mousedown', '.stop-propagation', (e) => {
		e.stopPropagation();
		e.stopImmediatePropagation();
	});
	// Make sure the disabled class make the element truly disabled
	$B.on('click.disabled', 'a.disabled, button.disabled, span.disabled, .btn.disabled', false);
	// Prevent default actions
	$B.on('click.prevent-default', '[data-prevent-default], [data-fancybox-close]', function(e) {
		// Continue as normal for cmd clicks etc
		if (e.which > 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
			return;
		}

		const $trigger = $(this);
		// Only if in a fancy container
		if ($trigger.is('[data-fancybox-close]') && $trigger.closest('.fancy-wrapper').length === 0) {
			// Not in a fancy container - do nothing
			return;
		}

		e.preventDefault();
	});

	// Prevent double-click select on label/legend
	$B.on('mousedown', 'label, legend', (e) => {
		if (e.detail > 1) {
			e.preventDefault();
		}
	});

	// Handle history back on click
	$B.on('click.history-back', '.history-back', (e, _data) => {
		if (window.history.length > 1) {
			e.preventDefault();
			e.stopImmediatePropagation();
			e.stopPropagation();
			window.history.back();
		}
	});

	// Handle click redirects
	mpro.handleClickRedirect('[data-href]', 'data-href', 'data-target');

	// Change url from select delegate binding
	// ---------------------------------------
	$B.on('change.redirect-location', 'select[data-onchange-url]', function(e) {
		const jqThis = $(this);
		const url = jqThis.find('option:selected').data('url');
		if (url) {
			mpro.setLocation(url, { el: this });
		}
		else {
			const val = jqThis.val();
			const value = Array.isArray(val) ? val.join(',') : String(val);
			mpro.setLocation(mpro.updateUrlParams(window.location.href, jqThis.attr('name'), value), { el: this });
		}
	});

	// $elem.find('[data-type-selector]').on('click', (e) => {
	// 	const $this = $(e.currentTarget);
	// 	const value = $this.data('value');

	// 	switch (value) {
	// 		case 'default': {
	// 			$elem.find('[data-color-selector]').addClass('d-none').prop('disabled', true);
	// 			$elem.find('[data-image-selector]').addClass('d-none').prop('disabled', true);

	// 			break;
	// 		}
	// 		case 'color': {
	// 			$elem.find('[data-color-selector]').removeClass('d-none').prop('disabled', false);
	// 			$elem.find('[data-image-selector]').addClass('d-none').prop('disabled', true);

	// 			break;
	// 		}
	// 		case 'image': {
	// 			$elem.find('[data-color-selector]').addClass('d-none').prop('disabled', true);
	// 			$elem.find('[data-image-selector]').removeClass('d-none').prop('disabled', false);

	// 			break;
	// 		}
	// 		// No default
	// 	}

	// 	$elem.find('[data-type-input]').val(value);
	// 	$this.addClass('active').siblings().removeClass('active');
	// });

	// Date range widget
	// ---------------------------------------
	$B.on('click', '[data-widget-date-range] [data-enable-selection]', async (e) => {
		const $enableSelection = $(e.currentTarget);
		const $elem = $enableSelection.closest('[data-widget-date-range]');

		if ($enableSelection.prop('checked') === true) {
			$elem.find('[data-dates-container]').removeClass('d-none');
			$elem.find('[data-dates-container]').find('input').prop('disabled', false);
		}
		else {
			$elem.find('[data-dates-container]').addClass('d-none');
			$elem.find('[data-dates-container]').find('input').prop('disabled', true);
		}
	});

	// Datetime range widget
	// ---------------------------------------
	$B.on('click', '[data-widget-date-time-range] [data-enable-selection]', async (e) => {
		const $enableSelection = $(e.currentTarget);
		const $elem = $enableSelection.closest('[data-widget-date-time-range]');

		if ($enableSelection.prop('checked') === true) {
			$elem.find('[data-dates-container]').removeClass('d-none');
			$elem.find('[data-dates-container]').find('input').prop('disabled', false);
		}
		else {
			$elem.find('[data-dates-container]').addClass('d-none');
			$elem.find('[data-dates-container]').find('input').prop('disabled', true);
		}
	});

	// Filter text widget
	// ---------------------------------------
	$B.on('keydown blur change', '[data-widget-filter-text] input[data-value]', (e) => {
		const $value = $(e.currentTarget);
		const $elem = $value.closest('[data-widget-filter-text]');
		if ($value.val() === '') {
			$elem.find('[data-operator-input]').attr('data-skip', '');
		}
		else {
			$elem.find('[data-operator-input]').removeAttr('data-skip');
		}
	});
	$B.on('clear', '[data-widget-filter-text] input[data-value]', (e) => {
		const $value = $(e.currentTarget);
		const $elem = $value.closest('[data-widget-filter-text]');
		$elem.find('[data-operator-selector]').first().trigger('click');
	});
	$B.on('click', '[data-widget-filter-text] [data-operator-selector]', (e) => {
		const $selector = $(e.currentTarget);
		const value = $selector.data('value');
		const $elem = $selector.closest('[data-widget-filter-text]');
		const $input = $elem.find('input[data-value]');

		// @ts-ignore
		if (['isempty', 'notempty'].includes(value)) {
			$input.val('').prop('disabled', true);
			$elem.find('[data-operator-input]').removeAttr('data-skip');
		}
		else {
			if ($input.val() === '') {
				$input.prop('disabled', false);
				$elem.find('[data-operator-input]').attr('data-skip', '');
			}
		}

		$elem.find('[data-operator-input]').val(value);
		$selector.addClass('active').siblings().removeClass('active');
		$elem.find('[data-icon-selected]').html($selector.find('[data-icon]').html()).attr({ 'data-bs-original-title': $selector.find('[data-tooltip]').html() });

		$elem.trigger('change');
	});

	// Filter number widget
	// ---------------------------------------
	$B.on('keydown blur change', '[data-widget-filter-number] input[data-value], [data-widget-filter-number] input[data-value-max]', (e) => {
		const $input = $(e.currentTarget);
		const $elem = $input.closest('[data-widget-filter-number]');
		const $value = $elem.find('[data-value]');
		const $valueMax = $elem.find('[data-value-max]');
		if ($value.val() === '' && $valueMax.val() === '') {
			$elem.find('[data-operator-input]').attr('data-skip', '');
		}
		else {
			$elem.find('[data-operator-input]').removeAttr('data-skip');
		}
	});
	$B.on('click', '[data-widget-filter-number] [data-operator-selector]', (e) => {
		const $selector = $(e.currentTarget);
		const value = $selector.data('value');
		const $elem = $selector.closest('[data-widget-filter-number]');

		const input = $elem.find('[data-number] input');
		const input_max = $elem.find('[data-number-max] input');

		if (value === 'between') {
			$elem.find('[data-number-max]').removeClass('d-none').addClass('last-child');
			$elem.find('[data-number]').removeClass('last-child');

			input.attr('placeholder', input.attr('data-placeholder'));
			input_max.attr('placeholder', input_max.attr('data-placeholder'));
		}
		else {
			$elem.find('[data-number-max]').addClass('d-none').removeClass('last-child');
			$elem.find('[data-number]').addClass('last-child');

			input.removeAttr('placeholder');
			input_max.removeAttr('placeholder');
		}

		$elem.find('[data-operator-input]').val(value);
		$selector.addClass('active').siblings().removeClass('active');
		$elem.find('[data-icon-selected]').html($selector.find('[data-icon]').html()).attr({ 'data-bs-original-title': $selector.find('[data-tooltip]').html() });

		$elem.trigger('change');
	});

	// Dynamic filter row form type
	// ---------------------------------------
	$B.on('change', '[data-widget-dynamic-row] [data-type-selector]', (e) => {
		const $selector = $(e.currentTarget);
		const $elem = $selector.closest('[data-widget-dynamic-row]');
		const $selected_row = $elem.find(`[data-type=${$selector.val()}]`);
		$elem.find('[data-type]').addClass('d-none');
		$selected_row.removeClass('d-none');
	});

	$B.on('click', '[data-widget-dynamic] [data-widget-dynamic-remove]', (e) => {
		const $selector = $(e.currentTarget);
		const $elem = $selector.closest('[data-widget-dynamic-row]');
		const $row = $elem.is('tr') ? $elem : $elem.parent();

		const $widget = $selector.closest('[data-widget-dynamic]');

		if ($widget.is('[data-disabled]')) {
			return;
		}

		if ((typeof $widget.data('min') !== 'undefined') && ($widget.find('[data-widget-dynamic-row]').length <= $widget.data('min'))) {
			return;
		}

		const event = $.Event('dynamic-row-before-remove');
		// Trigger row removed event
		$widget.trigger(event, [$row]);

		if ($row.find('input:visible, textarea:visible, select').filter(function() {
			// @ts-ignore
			return ($(this).val() !== '') && ($(this).val().length > 0);
		},
		).length > 0) {
			$widget.trigger('change');
		}

		if (!event.isDefaultPrevented()) {
			$row.remove();
			$widget.trigger('dynamic-row-after-remove', []);
		}

		e.preventDefault();
	});

	// Number form type
	// ---------------------------------------
	$B.on('change', '[data-widget-fields-rules-row] [data-action-selector]', (e) => {
		const $selector = $(e.currentTarget);
		const $elem = $selector.closest('[data-widget-fields-rules-row]');

		// @ts-ignore
		if (['replacement', 'regexp'].includes($selector.val())) {
			$selector.parent().removeClass('last-child');
			$elem.find('[data-field-from]').removeClass('d-none');
			$elem.find('[data-field-to]').removeClass('d-none').addClass('last-child');
		}
		else {
			$selector.parent().addClass('last-child');
			$elem.find('[data-field-from]').addClass('d-none');
			$elem.find('[data-field-to]').addClass('d-none').removeClass('last-child');
		}
	});

	// Timezone detection
	let cookieTimezone;
	let jsTimezone;
	try {
		jsTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		cookieTimezone = mpro.Cookies.get('jstz');
	}
	catch (error) {
		console.warn('Error reading timezone: %o', error);
	}

	// Timezone info not in cookie
	if (jsTimezone && !cookieTimezone) {
		// Detect the timezone and save it in cookie (for 1 day)
		mpro.Cookies.set('jstz', jsTimezone, { expires: 1 });
	}

	/**
	 * Get actions height
	 *
	 * @type {HTMLElement}
	 */
	const actionsWrap = document.querySelector('#actions-wrap');
	if (actionsWrap) {
		const setAH = function() {
			document.documentElement.style.setProperty('--actions-height', `${actionsWrap.offsetHeight}px`);
		};
		setAH();
		window.addEventListener('resize', setAH, { passive: true });
	}

	// Handle websocket messages
	$D.on('mp:ws-message', (e, data) => {
		// Parse message
		if (data && data.message) {
			const { message: response } = data;

			try {
				// Handle the response in a generic way
				return void handleRemoteResponse(new PartialContent({
					url: 'websocket-message',
					response,
				}));
			}
			catch (error) {
				console.warn('Error handling remote response from websocket response: WS data: %o\nError: %o', data, error);
			}
		}
	});

	// TODO: make all widgets 'lazy'
	const { 'default': App } = await import('../theme/app.js');

	// Init theme app
	(new App()).init();
});

/**
 * Hook into a did-navigate-in event (when page is first loaded or when navigating to a new page using ajax navigation)
 *
 * @param {{ type: 'load' | 'ajax', $containers: JQuery }} param0
 */
// @ts-ignore
mpro.on('did-navigate-in', ({ type, $containers }) => {

	// console.log('did-navigate-in', type, $containers);

	// Disable history scroll restoration
	if (window.location.hash && ('scrollRestoration' in window.history)) {
		// Disable popstate auto-scrolling
		window.history.scrollRestoration = 'manual';
	}

	// Auto-Scroll to hash element with taking into account the header offset
	const docHash = $.escapeSelector(window.location.hash.replace('#', ''));
	if (docHash && !Number.isInteger(docHash)) {
		let $scrollToElement = $(`#${docHash}`);
		if ($scrollToElement.length === 0) {
			$scrollToElement = $(`a[name="${docHash}"]`);
		}
		// Hash is already set
		if ($scrollToElement.length > 0) {
			// No need to 'blink' from js, there is a css rule that takes care of the blinking animation (_scroll_target.scss)
			setTimeout(() => {
				mpro.scrollToElement($scrollToElement, { speed: 266, focusMiddle: false, blink: false });
			}, 300);
		}
	}
});